// components/organisms/SearchConsultantForm.tsx
"use client"

import { useEffect, useState } from "react"
import { toast } from "sonner"
import { Search } from "lucide-react"

import { SearchButton } from "@/components/molecules/SearchButton"
import PlaceSearchInput from "@/components/molecules/PlaceSearch"
import { getProfessions } from "@/actions/user/user.action"
import { Dropdown } from "@/components/molecules/Dropdown"
import { LocationData } from "@/app/stores/places"
import { Category, ConsultantFilters } from "@/app/types"

interface SearchConsultantFormProps {
  onSubmit: (filters: ConsultantFilters) => void
  defaultFilters?: ConsultantFilters
}

export const SearchConsultantForm = ({ 
  onSubmit, 
  defaultFilters,
  translations 
}: SearchConsultantFormProps & { translations: Record<string, any> }) => {
  const [professions, setProfessions] = useState<Category[]>([])
  const [selectedProfession, setSelectedProfession] = useState<Category>({ id: "", name: translations.all })
  const [location, setLocation] = useState<LocationData | null>(null)

  useEffect(() => {
    const fetchProfessions = async () => {
      try {
        const response = await getProfessions()
        const fetched = response.data?.data ?? []
        setProfessions([{ id: "", name: translations.all }, ...fetched])
      } catch {
        toast.error("Failed to load professions.")
      }
    }

    fetchProfessions()
  }, [])

  useEffect(() => {
    if (!professions.length) return

    if (defaultFilters?.profession?.id) {
      const match = professions.find((p) => p.id === defaultFilters.profession!.id)
      if (match) setSelectedProfession(match)
    }

    if (defaultFilters?.location) {
      setLocation(defaultFilters.location)
    }
  }, [defaultFilters, professions])

  const handleSearch = () => {
    onSubmit({
      profession: selectedProfession?.id ? selectedProfession : undefined,
      location: location || undefined
    })
  }

  return (
    <section className="relative z-40 w-full">
      <div className="mx-auto max-w-screen-2xl">
        <form
          onSubmit={(e) => {
            e.preventDefault()
            handleSearch()
          }}
          className="relative flex flex-col gap-4 rounded-lg bg-white px-5 py-11 shadow-md dark:bg-black lg:flex-row lg:gap-x-5"
        >
          <Dropdown
            label={translations.professionPlaceholder}
            items={professions}
            value={selectedProfession}
            onChange={setSelectedProfession}
          />

          <PlaceSearchInput
            borderless
            value={location?.address ?? ""}
            onChange={(newLocation) => setLocation(newLocation as LocationData)}
          />

          <SearchButton
            icon={<Search size={24} />}
            label={translations.button}
            type="submit"
            className="property-search-primary"
          />
        </form>
      </div>
    </section>
  )
}